import {
  Alert,
  Button,
  Card,
  Col,
  Dropdown,
  Menu,
  message,
  Popconfirm,
  Row,
  Space,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from "antd";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Link, Prompt, useParams } from "react-router-dom";
import Icon from "../../assets/icons/icon";
import AnimateIn from "../../components/animateIn";
import CardBox from "../../components/cardBox";
import ConflictAlert from "../../components/conflictAlert";
import Details from "../../components/detailsList";
import { renderErrorMessage } from "../../components/messages/errorMessage";
import {
  TaxLegalCaseStoreProvider,
  useTaxLegalActions,
  useTaxLegalCase,
  useTaxLegalEvaluation
} from "../../contexts/taxLegalCaseStore";
import TaxLegalStagesStore, { StageFormNames, useTaxLegalStages } from "../../contexts/TaxLegalStagesStore";
import _t from "../../lang/translate";
import PageBody from "../../layout/pageBody";
import PageTitle from "../../layout/pageTitle";
import { getPath } from "../../routes/appRoutes";
import { getEnumTitleById } from "../../services/enumService";
import {
  taxLegalCaseToggleStatus,
  taxLegalCaseUrl
} from "../../services/taxLegalService";
import { useApi } from "../../services/useApi";
import { ParamTypes, ScreenProps } from "../../types/appTypes";
import {
  initTaxLegalCase,
  TaxLegalCase as TaxLegalCaseType,
  TaxLegalStage
} from "../../types/taxLegalTypes";
import { date } from "../../utilities/date";
import { compare } from "../../utilities/deepCompare";
import format from "../../utilities/formatNumbers";
import { findTagTitleFromId } from "../../utilities/mapPriceSourceTags";
import CaseTimeLine from "./caseTimeLine/caseTimeLine";
import TaxLegalCaseHead from "./header/taxLegalCaseHead";
import TaxLegalHeadExtra from "./header/taxLegalHeadExtra";
import PricingReceivablesCard from "./pricing/pricingReceivablesCard";
import Complaint from "./stages/complaint";
import PriceCheck from "./stages/priceCheck";
import Resumption from "./stages/resumption";
import TaxLegalCaseAttachments from "./taxLegalAttachments";
import LayoutStore from "../../contexts/layoutStore";


const TaxLegalCase: FC<ScreenProps> = ({ title }) => {
  const { id } = useParams<ParamTypes>();
  const [{ data, isLoading }] = useApi<TaxLegalCaseType>(
    taxLegalCaseUrl(id),
    initTaxLegalCase,
    true,
  );
  const caseReceived = data.id > 0;
  return (
    <AnimateIn start={!isLoading && caseReceived} loadingBeforeStart>
      <TaxLegalCaseStoreProvider initialState={data}>
        <TaxLegalStagesStore.Provider initialState={data.stages}>
          <PromptWrapper />
          <PageTitle
            title={`${title} / ${id}`}
            fluid
            headerMargin={false}
            backBtn
            extra={<TaxLegalHeadExtra />}
            footer={caseReceived && <TaxLegalCaseHead />}
          />
          <PageBody>
            <TaxLegalCaseLayout />
          </PageBody>
        </TaxLegalStagesStore.Provider>
      </TaxLegalCaseStoreProvider>
    </AnimateIn>
  );
};
export default TaxLegalCase;

function PromptWrapper() {
  const taxLegalCase = useTaxLegalCase();
  const { getStagesForms, stages } = useTaxLegalStages();
  const taxLegalCaseRef = useRef(taxLegalCase);

  const compareCase = useCallback(() => {
    return compare(taxLegalCase, taxLegalCaseRef.current);
  }, [taxLegalCase, taxLegalCaseRef.current]);

  useEffect(() => {
    taxLegalCaseRef.current = taxLegalCase;
  }, [taxLegalCase]);


  const compareStagesStateToForms = useCallback(() => {
    const stagesForms = getStagesForms();
    for (const stage of stages) {
      const formStage = stagesForms.get(stage.fingerprint)
      if (!formStage) continue;
      for (const type in stage.data) {
        const stageData = stage.data?.[type as StageFormNames]
        const formData = formStage.data[type as StageFormNames]?.getFieldsValue(true)
        if (!stageData || Array.isArray(stageData) || !formData || typeof stageData !== 'object') continue;
        const equal = compare(stageData, formData)
        if (!equal) return false;
      }
    }
    return true;
  }, [stages])


  const handleMessage = useCallback(() => {
    const caseUpToDate = compareCase();
    const equalStagesStateAndForm = compareStagesStateToForms()
    let isUpToDate = !!(caseUpToDate && equalStagesStateAndForm);
    if (isUpToDate) return true;
    return _t("msg.confirm_leave");
  }, [compareCase, compareStagesStateToForms]);

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      const message = handleMessage();
      if (typeof message === "string") {
        event.preventDefault();
        event.returnValue = message; // Legacy
        return message; // Legacy
      }
    },
    [handleMessage],
  );

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [handleBeforeUnload]);

  return <Prompt message={handleMessage} />;
}

function TaxLegalCaseLayout() {
  const { note } = useTaxLegalCase();
  const { screen } = LayoutStore.useContainer()
  const isBelowXxl = !screen.greaterThan?.xxl
  return (
    <Row align="top" gutter={24}>
      <Col xxl={{ span: 7 }} xl={{ span: 12 }} xs={{ span: 24 }}>
        <CaseStatusCard />
        {note && <Alert message={_t("noteAtCreation")} description={note} closable />}
        {isBelowXxl && <SideContent />}
      </Col>
      <Col xxl={{ span: 9 }} xl={{ span: 12 }} xs={{ span: 24 }}>
        <CaseStages />
        <CaseTimeLine />
      </Col>
      {!isBelowXxl && (
        <Col xxl={{ span: 8 }} xl={{ span: 12 }} xs={{ span: 24 }}>
          <SideContent />
        </Col>
      )}
    </Row>
  );
}
function SideContent() {
  const { importantNote, relatedCases } = useTaxLegalCase();
  return (
    <>
      {importantNote && <Alert style={{ marginBottom: 24 }} message={importantNote} />}
      {relatedCases && <ConflictAlert conflictedCases={relatedCases} />}
      <Card bordered={false} style={{ boxShadow: "none" }} title={_t("attachments")}>
        <TaxLegalCaseAttachments />
      </Card>
      <PricingReceivablesCard />
    </>
  )
}

function CaseStatusCard() {
  const {
    governmentBatchId,
    penaltyCaseId,
    penaltyType,
    nextDeadline,
    activeStageValues,
    allDMRDocumentsRequestedAt,
    obsoletedAt,
    repairRequestedAt,
    createdAt,
    id,
  } = useTaxLegalCase();
  const { setTaxLegalCase } = useTaxLegalActions()
  const evaluation = useTaxLegalEvaluation();
  const governmentNewPriceAssessment = getEnumTitleById(
    activeStageValues?.governmentNewPriceAssessment ?? 0,
    ["taxLegalCase", "governmentAssessment"],
  );
  const governmentTradePriceAssessment = getEnumTitleById(
    activeStageValues?.governmentTradePriceAssessment ?? 0,
    ["taxLegalCase", "governmentAssessment"],
  );
  const [loading, setLoading] = useState(false)

  const toggleObsoleted = (checked: boolean) => toggleBase(async () => (await taxLegalCaseToggleStatus(id, { obsoleted: checked ? true : false })).data)
  const toggleRepairRequested = (checked: boolean) => toggleBase(async () => (await taxLegalCaseToggleStatus(id, { repairRequested: checked ? true : false })).data)
  const toggleDocumentAccess = (checked: boolean) => toggleBase(async () => (await taxLegalCaseToggleStatus(id, { allDMRDocumentsRequested: checked ? true : false })).data)



  async function toggleBase(callback: () => Promise<TaxLegalCaseType>) {
    try {
      setLoading(true)
      const newTaxLegalCase = await callback()
      setTaxLegalCase(newTaxLegalCase)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoading(false)
    }
  }
  return (
    <Card
      title={_t("status")}
      extra={
        <Space>
          {!!obsoletedAt && (
            <Typography.Text type={"warning"}>
              <Tooltip title={`${_t("archived", 'date_at')} ${format.dateAndTime(obsoletedAt)}`}>
                <Icon name="time-outline" size="large" style={{ display: "block" }} />
              </Tooltip>
            </Typography.Text>
          )}
        </Space>
      }
    >
      <Details borderLess>
        <Details.Item
          label={_t("next", "deadline")}
          value={nextDeadline ? date(nextDeadline) : "-"}
        />
        <Details.Item
          label={_t("conclusionDate")}
          value={activeStageValues?.conclusionDate ? date(activeStageValues.conclusionDate) : "-"}
        />
        <Details.Item
          label={_t("limitationDate")}
          value={
            activeStageValues?.statuteOfLimitationsDate
              ? date(activeStageValues.statuteOfLimitationsDate)
              : "-"
          }
        />
        <Details.Divider />

        <Details.Item
          label={_t("actual", "regulation")}
          value={format.price(Number(activeStageValues?.regulationAmount || ""))}
        />
        <Details.Item
          label={_t("eventually", "reduction")}
          value={format.price(Number(activeStageValues?.regulationReduction || ""))}
        />

        {penaltyType?.id === 4 && !!penaltyCaseId && (
          <Details.Item label={_t("penaltyCaseId")} value={penaltyCaseId ?? "-"} />
        )}
        {!!governmentBatchId && (
          <Details.Item label={_t("ms.groupCaseNumber")} value={governmentBatchId} />
        )}
        <Details.Item
          style={{ alignItems: "center" }}
          label={_t("MSAssessment")}
          value={`${governmentNewPriceAssessment ? governmentNewPriceAssessment + " NP" : ""} - ${governmentTradePriceAssessment ? governmentTradePriceAssessment + " HP" : ""}`}
        />
        <Details.Divider />
        <Details.Item
          label={_t("accessToDocuments")}
          value={
            <Switch size="small"
              checked={!!allDMRDocumentsRequestedAt}
              onChange={toggleDocumentAccess}
              loading={loading}
            />
          }
        />
      </Details>
      <CardBox title={<Typography.Title level={5}>{_t("original", "evaluation")}</Typography.Title>} >
        <Details borderLess>
          <Details.Item label={_t("reviewDate")} value={format.date(evaluation?.reviewDate)} />
          <Details.Item label={
            <>
              {_t("reporter")}{" "}
              {evaluation?.dmfCaseId && (
                <>
                  (<Link to={getPath("tax", evaluation?.dmfCaseId)}>
                    {evaluation?.dmfCaseId ?? "-"}
                  </Link>)
                </>
              )}
            </>
          } value={evaluation?.reporterCompany ?? "-"} />
          <Details.Divider />
          <Details.Item label={_t("new_price")} value={format.price(evaluation?.newPrice)} />
          <Details.Item label={_t("new_price", "without_equipments")}
            value={format.price((evaluation?.newPrice ?? 0) - (evaluation?.equipmentsPrice ?? 0))}
          />
          <Details.Item
            label={_t("extra_equipment_price")}
            value={format.price(evaluation?.equipmentsPrice)}
          />
          <Details.Item label={_t("trade_price")} value={format.price(evaluation?.tradePrice)} />
          <Details.Item
            label={_t("registration_fee")}
            value={format.price(evaluation?.registrationFee)}
          />
          <Details.Item
            label={_t("validatedMileage")}
            value={format.yesOrNo(evaluation?.mileageDocumentation)}
          />

          <Details.Item
            label={_t("new_price", "source")}
            value={
              findTagTitleFromId(evaluation?.newPriceSourceTag || -1) && (
                <Tag color="gray" style={{ fontSize: 12, lineHeight: "16px" }}>
                  {findTagTitleFromId(evaluation?.newPriceSourceTag || -1)}
                </Tag>
              )
            }
          />
          <Details.Item
            label={_t("equipment_price", "source")}
            value={evaluation?.equipmentPriceSourceTags?.map((id) => (
              <Tag color="gray" style={{ fontSize: 12, lineHeight: "16px" }} key={id}>
                {findTagTitleFromId(id)}
              </Tag>
            ))}
          />
        </Details>
      </CardBox>
      <Details className="mt-1" borderLess>
        <Details.Item
          label={_t("archived")}
          value={
            <Switch size="small"
              checked={!!obsoletedAt}
              onChange={toggleObsoleted}
              loading={loading}
            />
          }
        />
        <Details.Item
          label={_t("repairRequired")}
          value={
            <Switch size="small"
              checked={!!repairRequestedAt}
              onChange={toggleRepairRequested}
              loading={loading}
            />
          }
        />

      </Details>
      <div className="text-right">
        <small>{_t("case_created", "date_at")} {format.date(createdAt)}</small>
      </div>
    </Card>
  );
}


function CaseStages() {
  const { stages, deleteStage, addStage, reorderStages, activeTab, setActiveTab } = useTaxLegalStages();
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const currentRef = useRef<HTMLDivElement | null>(null);

  function handleMouseDown(index: number) {
    if (refs.current) {
      currentRef.current = refs.current[index];
    }

    let item = currentRef.current;
    if (!item) return;
    const bounds = item.parentElement?.parentElement?.parentElement?.parentElement;
    if (bounds) {
      item.dataset.minX = String(bounds.getBoundingClientRect().left);
      item.dataset.maxX = String(bounds.getBoundingClientRect().right);
    }
    item.dataset.initX = String(item.getBoundingClientRect().x);
    window.addEventListener("mousemove", handleDragElement);
    window.addEventListener("mouseup", handleMouseUp);
  }
  function handleMouseUp(ev: MouseEvent) {
    let item = currentRef.current;
    if (!item) return;
    const x = ev.clientX;
    if (refs.current) {
      const insertionIndex = refs.current.findIndex((tab) => {
        const rect = tab?.getBoundingClientRect();
        if (tab === item) return false;
        if (rect && rect.left <= x && rect.right >= x) {
          return true;
        }
        return false;
      });
      const itemIndex = refs.current.indexOf(item);
      if (insertionIndex > -1 && itemIndex > -1) {
        reorderStages(insertionIndex, itemIndex);
      }
    }
    item.style.transform = "translate(0px)";
    delete item.dataset.initMouseX;
    window.removeEventListener("mouseup", handleMouseUp);
    window.removeEventListener("mousemove", handleDragElement);
    currentRef.current = null;
  }

  function handleDragElement(ev: MouseEvent) {
    let item = currentRef.current;
    if (item) {
      const offset = parseInt(item.dataset.initX || "0");
      const min = parseInt(item.dataset.minX || "0");
      const max = parseInt(item.dataset.maxX || "0");

      if (!item.dataset.initMouseX) {
        item.dataset.initMouseX = String(ev.clientX - offset);
      }

      const initMouseX = parseInt(item.dataset.initMouseX || "0");
      const minX = min - initMouseX - offset;
      const maxX = max - initMouseX - offset;
      let nextX = ev.clientX - initMouseX - offset;
      if (nextX < minX) nextX = minX;
      else if (nextX > maxX) nextX = maxX;
      item.style.transform = `translateX(${nextX}px)`;
    }
  }

  function confirmDelete(stage: TaxLegalStage) {
    let shouldDelete = false;
    const label = stage.type === "complaint" ? _t("complaint") : _t("resumption");
    if (window.prompt("Indtast 'SLET' for at slette " + label) === "SLET") {
      if (window.confirm(_t("msg.confirm_delete"))) {
        shouldDelete = true;
      }
    }
    if (shouldDelete) {
      deleteStage(stage.fingerprint);
      message.warning(label + " " + _t("deleted").toLowerCase());
    } else {
      message.info(label + " " + _t("msg.not_deleted").toLowerCase());
    }
  }

  return (
    <Tabs
      className="taxlegal-case-management"
      tabBarExtraContent={
        <Dropdown
          placement="bottomRight"
          overlay={
            <Menu>
              <Menu.Item key={"addComplaint"} onClick={() => addStage("complaint")}>
                {_t("add", "complaint")}
              </Menu.Item>
              <Menu.Item key={"addResumption"} onClick={() => addStage("resumption")}>
                {_t("add", "resumption")}
              </Menu.Item>
            </Menu>
          }
        >
          <Button size="small">+</Button>
        </Dropdown>
      }
      activeKey={activeTab}
      onChange={setActiveTab}
    >
      {stages.map((stage, i) => {
        if (stage.type === "price_check") {
          return (
            <Tabs.TabPane
              animated
              forceRender
              tab={
                <>
                  Pristjek{" "}
                  <sub className="text-secondary">
                    {stage.timestamp ? date(stage.timestamp) : ""}
                  </sub>
                </>
              }
              key={stage.fingerprint}
            >
              <PriceCheck fingerprint={stage.fingerprint} position={"0"} />
            </Tabs.TabPane>
          );
        }
        if (stage.type === "complaint") {
          return (
            <Tabs.TabPane
              forceRender
              tab={
                <div
                  className="custom-tab"
                  ref={(el) => (refs.current[i] = el)}
                  onMouseDown={() => handleMouseDown(i)}
                >
                  <span className="mr-05">
                    Klagesag{" "}
                    <sub className="text-secondary">
                      {stage.timestamp ? date(stage.timestamp) : ""}
                    </sub>
                  </span>
                  <Popconfirm
                    title={_t("confirm_action")}
                    okText={_t("delete")}
                    onConfirm={() => confirmDelete(stage)}
                  >
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      type="text"
                      size="small"
                      danger
                      icon={<Icon name="trash-bin-outline" />}
                    />
                  </Popconfirm>
                </div>
              }
              key={stage.fingerprint}
            >
              <Complaint fingerprint={stage.fingerprint} position={stage.position} />
            </Tabs.TabPane>
          );
        }
        if (stage.type === "resumption") {
          return (
            <Tabs.TabPane
              forceRender
              tab={
                <div
                  className="custom-tab"
                  ref={(el) => (refs.current[i] = el)}
                  onMouseDown={() => handleMouseDown(i)}
                >
                  <span className="mr-05">
                    {_t("resumption")}{" "}
                    <sub className="text-secondary">
                      {stage.timestamp ? date(stage.timestamp) : ""}
                    </sub>
                  </span>
                  <Popconfirm
                    title={_t("confirm_action")}
                    okText={_t("delete")}
                    onConfirm={() => confirmDelete(stage)}
                  >
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      type="text"
                      size="small"
                      danger
                      icon={<Icon name="trash-bin-outline" />}
                    />
                  </Popconfirm>
                </div>
              }
              key={stage.fingerprint}
            >
              <Resumption fingerprint={stage.fingerprint} position={stage.position} />
            </Tabs.TabPane>
          );
        }
        return null;
      })}
    </Tabs>
  );
}
